/**
 * Secure Fields unlock context.
 *
 * Single source of truth for "do we currently hold the org-wide AES key in
 * memory, and if so can we encrypt/decrypt with it." Mounted near the root
 * (`app/_layout.tsx`) so every form, list, and print page can call
 * `useSecureUnlock()` without prop-drilling.
 *
 * Key handling rules:
 *  - The derived 32-byte AES key is held in a `useRef`, never in `useState`,
 *    because state ends up in React DevTools and React's render tree. The ref
 *    is a private slot.
 *  - We never serialize the key to AsyncStorage / SecureStore / cookies; full
 *    re-derivation (PBKDF2 ~500ms) is required after every reload. That's the
 *    point — the threat model assumes the device may be physically taken.
 *  - On `lock()` we zero the buffer before dropping the ref. Defense-in-depth;
 *    can't undo engine-level copies but costs nothing.
 *
 * Idle timeout: any pointer/key/touch activity bumps the deadline. After
 * `IDLE_LOCK_MS` of silence we auto-lock. 5 minutes — short window per the
 * "secure-fields should be visible briefly only" rule. Re-unlock requires the
 * shared org password again.
 *
 * Throttle: 3 wrong attempts within 60s blocks further attempts until the
 * window expires. Cheap brute-force speed bump on top of PBKDF2's intrinsic
 * cost — a real attacker would have to wait both the lockout AND the ~500ms
 * KDF for every guess.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { organizationService } from './organization-service';

import type { Organization } from '@/types/models';
import {
	buildVerifier,
	checkVerifier,
	decodeSalt,
	deriveKey,
	decryptString,
	encodeSalt,
	encryptString,
	generateSalt,
	KDF_ITERS_DEFAULT,
	zeroKey,
} from '@/utils/secure-crypto';


const IDLE_LOCK_MS = 5 * 60 * 1000; // 5 min — see file header for rationale
const THROTTLE_WINDOW_MS = 60 * 1000;
const THROTTLE_MAX_ATTEMPTS = 3;

// Web activity events. Native uses screen focus/blur (handled elsewhere if needed).
const ACTIVITY_EVENTS: ReadonlyArray<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'touchstart'];

export interface SecureUnlockContextValue {
	/** True iff the org has a verifier configured (i.e. a password has been set up). */
	isReady: boolean;
	/** True iff the key is currently in memory and the session is not locked. */
	isUnlocked: boolean;
	/** True while we're still loading the organization row on first mount. */
	isLoading: boolean;
	/** Most recent throttle expiry in ms-since-epoch; 0 if not throttled. */
	throttledUntil: number;
	/** The org id this context is bound to (the verifier was issued for it). */
	orgId: string | null;
	/** Refresh org row from API — call after a setup or reset PUT. */
	refresh: () => Promise<void>;
	/** Returns true on success. False means wrong password or throttled. */
	unlock: (password: string) => Promise<boolean>;
	/** Drop the key. Idempotent. */
	lock: () => void;
	/** Encrypt UTF-8 string with the in-memory key. Throws if locked. */
	encrypt: (plaintext: string) => string;
	/** Decrypt an `sf1:` blob. Throws if locked or blob is malformed/tampered. */
	decrypt: (blob: string) => string;
	/**
	 * First-time setup: derive a fresh key from `password`, build a verifier,
	 * PUT it to /organization, and leave the session unlocked with the new key.
	 * Caller must be admin (server enforces).
	 */
	setupPassword: (password: string) => Promise<void>;
	/**
	 * Rotate the password. Wipes every ciphertext on the server side (so users
	 * MUST re-enter every secure value afterwards). Caller must be admin AND
	 * must pass `resetConfirmed: true` for the server to accept.
	 */
	resetPassword: (password: string) => Promise<void>;
}

const SecureUnlockContext = createContext<SecureUnlockContextValue | null>(null);

export const useSecureUnlock = (): SecureUnlockContextValue => {
	const ctx = useContext(SecureUnlockContext);
	if (!ctx) throw new Error('useSecureUnlock must be used inside <SecureUnlockProvider>');
	return ctx;
};

export const SecureUnlockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const keyRef = useRef<Uint8Array | null>(null);
	const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [org, setOrg] = useState<Organization | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isUnlocked, setIsUnlocked] = useState(false);
	const [attempts, setAttempts] = useState<number[]>([]);

	const isReady = !!org?.secureVerifier;
	const orgId = org?.id ?? null;

	const refresh = useCallback(async () => {
		try {
			const fresh = await organizationService.get();
			setOrg(fresh);
		} catch (err) {
			// Surface loud in dev; non-fatal — caller can retry.
			if (__DEV__) console.warn('[secure-unlock] org load failed', err);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => { void refresh(); }, [refresh]);

	const clearIdleTimer = useCallback(() => {
		if (idleTimerRef.current) {
			clearTimeout(idleTimerRef.current);
			idleTimerRef.current = null;
		}
	}, []);

	const lock = useCallback(() => {
		if (keyRef.current) {
			zeroKey(keyRef.current);
			keyRef.current = null;
		}
		clearIdleTimer();
		setIsUnlocked(false);
	}, [clearIdleTimer]);

	const bumpIdleTimer = useCallback(() => {
		clearIdleTimer();
		idleTimerRef.current = setTimeout(() => { lock(); }, IDLE_LOCK_MS);
	}, [clearIdleTimer, lock]);

	// Wire activity listeners on web while unlocked. Native devices auto-lock
	// when the app backgrounds via OS biometric prompts on re-foreground;
	// adding RN AppState handling here is a future enhancement.
	useEffect(() => {
		if (!isUnlocked) return;
		if (Platform.OS !== 'web' || typeof document === 'undefined') return;
		bumpIdleTimer();
		const onActivity = () => bumpIdleTimer();
		for (const ev of ACTIVITY_EVENTS) document.addEventListener(ev, onActivity, { passive: true });
		return () => {
			for (const ev of ACTIVITY_EVENTS) document.removeEventListener(ev, onActivity);
			clearIdleTimer();
		};
	}, [isUnlocked, bumpIdleTimer, clearIdleTimer]);

	// Zero the key if the provider unmounts (page navigation tear-down on web,
	// app close on native). Safety net beyond explicit lock().
	useEffect(() => () => lock(), [lock]);

	const recordFailedAttempt = useCallback(() => {
		const now = Date.now();
		setAttempts((prev) => [...prev.filter((t) => now - t < THROTTLE_WINDOW_MS), now]);
	}, []);

	const throttledUntil = useMemo(() => {
		const now = Date.now();
		const recent = attempts.filter((t) => now - t < THROTTLE_WINDOW_MS);
		if (recent.length < THROTTLE_MAX_ATTEMPTS) return 0;
		return Math.min(...recent) + THROTTLE_WINDOW_MS;
	}, [attempts]);

	// throttledUntil is time-derived — useMemo above only re-fires when
	// `attempts` changes. Without this timer the "throttled" banner can
	// remain visually active past the 60s window. When the window expires
	// we filter the stale attempts, which re-evaluates the memo to 0.
	useEffect(() => {
		const now = Date.now();
		const recent = attempts.filter((t) => now - t < THROTTLE_WINDOW_MS);
		if (recent.length < THROTTLE_MAX_ATTEMPTS) return;
		const earliest = Math.min(...recent);
		const expireMs = Math.max(0, earliest + THROTTLE_WINDOW_MS - now);
		const t = setTimeout(() => {
			const cutoff = Date.now() - THROTTLE_WINDOW_MS;
			setAttempts((prev) => prev.filter((ts) => ts > cutoff));
		}, expireMs);
		return () => clearTimeout(t);
	}, [attempts]);

	const unlock = useCallback(async (password: string): Promise<boolean> => {
		if (throttledUntil > Date.now()) return false;
		if (!org?.secureSalt || !org.secureVerifier || !org.id) return false;
		const iters = org.secureKdfIters ?? KDF_ITERS_DEFAULT;
		const salt = decodeSalt(org.secureSalt);
		const key = await deriveKey(password, salt, iters);
		if (!checkVerifier(key, org.secureVerifier, org.id)) {
			zeroKey(key);
			recordFailedAttempt();
			return false;
		}
		// Replace any prior key (re-unlocking after lock) before keeping new one.
		if (keyRef.current) zeroKey(keyRef.current);
		keyRef.current = key;
		setAttempts([]);
		setIsUnlocked(true);
		return true;
	}, [org, recordFailedAttempt, throttledUntil]);

	const requireKey = (): Uint8Array => {
		const k = keyRef.current;
		if (!k) throw new Error('Secure Fields are locked — call unlock() first');
		return k;
	};

	const encrypt = useCallback((plaintext: string): string => encryptString(requireKey(), plaintext), []);
	const decrypt = useCallback((blob: string): string => decryptString(requireKey(), blob), []);

	const setupPassword = useCallback(async (password: string) => {
		const orgRow = org;
		if (!orgRow?.id) throw new Error('organization not loaded yet');
		if (orgRow.secureVerifier) throw new Error('password already set — use resetPassword');
		const salt = generateSalt();
		const iters = KDF_ITERS_DEFAULT;
		const key = await deriveKey(password, salt, iters);
		const verifier = buildVerifier(key, orgRow.id);
		const setupAt = new Date().toISOString();
		await organizationService.update({
			secureSalt: encodeSalt(salt),
			secureVerifier: verifier,
			secureKdfIters: iters,
			secureSetupAt: setupAt,
		});
		if (keyRef.current) zeroKey(keyRef.current);
		keyRef.current = key;
		setIsUnlocked(true);
		await refresh();
	}, [org, refresh]);

	const resetPassword = useCallback(async (password: string) => {
		const orgRow = org;
		if (!orgRow?.id) throw new Error('organization not loaded yet');
		const salt = generateSalt();
		const iters = KDF_ITERS_DEFAULT;
		const key = await deriveKey(password, salt, iters);
		const verifier = buildVerifier(key, orgRow.id);
		const setupAt = new Date().toISOString();
		// resetConfirmed: true tells the server to NULL every existing ciphertext
		// — without it the PUT 409s when a verifier already exists.
		await organizationService.update({
			secureSalt: encodeSalt(salt),
			secureVerifier: verifier,
			secureKdfIters: iters,
			secureSetupAt: setupAt,
			// @ts-expect-error — server-only flag, not on Organization type
			resetConfirmed: true,
		});
		if (keyRef.current) zeroKey(keyRef.current);
		keyRef.current = key;
		setIsUnlocked(true);
		await refresh();
	}, [org, refresh]);

	const value = useMemo<SecureUnlockContextValue>(() => ({
		isReady,
		isUnlocked,
		isLoading,
		throttledUntil,
		orgId,
		refresh,
		unlock,
		lock,
		encrypt,
		decrypt,
		setupPassword,
		resetPassword,
	}), [
		isReady, isUnlocked, isLoading, throttledUntil, orgId,
		refresh, unlock, lock, encrypt, decrypt, setupPassword, resetPassword,
	]);

	return <SecureUnlockContext.Provider value={value}>{children}</SecureUnlockContext.Provider>;
};
